import { useEffect } from "react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { useCanonical } from "@/lib/useCanonical";

type Entry = {
  date: string; // ISO yyyy-mm-dd
  title: string;
  tags: ("shipped" | "improved" | "fixed" | "security")[];
  body: string;
};

// Newest first. Curate by hand for now; can migrate to a `changelog` table later.
const ENTRIES: Entry[] = [
  {
    date: "2026-07-28",
    title: "Energy Gap report",
    tags: ["shipped"],
    body: "A new sharable report at /energy-gap turns two weeks of check-ins into a plain-English summary, your best window, your hardest window, and the one habit most linked to your good days. No login required; bring your own numbers.",
  },
  {
    date: "2026-07-22",
    title: "Trust & Model card pages",
    tags: ["shipped"],
    body: "Two new pages: /trust explains where your data lives, who touches it, and how to reach us. /model-card explains what the energy engine does, and just as importantly, what it deliberately doesn't do.",
  },
  {
    date: "2026-07-14",
    title: "Waitlist skip-the-line",
    tags: ["shipped"],
    body: "The new /waitlist page gives every signup a personal referral link. Share it, and you move up the invite queue as friends join. Quietly, without leaderboards.",
  },
  {
    date: "2026-06-30",
    title: "Softer correlation language",
    tags: ["improved"],
    body: "Correlation cards now require 60 days of data, show a 95% confidence interval in plain language, and use Benjamini–Hochberg correction so we don't cry wolf on coincidences. Mockups are labeled 'illustrative only'.",
  },
  {
    date: "2026-06-07",
    title: "Public changelog",
    tags: ["shipped"],
    body: "We're opening the changelog. Every meaningful change to the product, new features, accuracy improvements, security fixes, will land here in plain English.",
  },
  {
    date: "2026-06-06",
    title: "Tighter database access rules",
    tags: ["security"],
    body: "Reviewed and tightened row-level policies on invite codes and buddy pairs. No user-visible change, but it closes two paths where one account could in principle have touched another's data.",
  },
  {
    date: "2026-05-20",
    title: "Myelina wearable correlations",
    tags: ["shipped"],
    body: "Insights now shows how your sleep, heart rate, and steps correlate with your check-in energy. Pearson coefficient and R² included, so you can see which signals actually predict your day.",
  },
  {
    date: "2026-05-03",
    title: "Visual refresh. AIKR Premium",
    tags: ["improved"],
    body: "New typography (Instrument Serif + Inter), warm cream background, and midnight-blue accents. Marketing pages now load with a calmer first impression.",
  },
];

const META_TITLE = "Changelog | Myelina Health";
const META_DESC =
  "Every meaningful change we ship to Myelina Health, features, accuracy improvements, security fixes. In plain English.";

const setMeta = (name: string, content: string, attr: "name" | "property" = "name") => {
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const TAG_STYLES: Record<Entry["tags"][number], string> = {
  shipped: "bg-primary/10 text-primary border-primary/20",
  improved: "bg-amber-500/10 text-amber-700 border-amber-500/20 dark:text-amber-300",
  fixed: "bg-emerald-500/10 text-emerald-700 border-emerald-500/20 dark:text-emerald-300",
  security: "bg-foreground/10 text-foreground border-foreground/20",
};

const formatDate = (iso: string) =>
  new Date(iso + "T00:00:00Z").toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });

const Changelog = () => {
  useCanonical({ path: "/changelog" });
  useEffect(() => {
    document.title = META_TITLE;
    setMeta("description", META_DESC);
    setMeta("og:title", META_TITLE, "property");
    setMeta("og:description", META_DESC, "property");
    // Set canonical
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", "/changelog");
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container mx-auto px-6 sm:px-10 lg:px-16 py-24 max-w-3xl">
        <header className="mb-14">
          <p className="text-[11px] tracking-[0.22em] uppercase text-muted-foreground mb-3">
            Changelog
          </p>
          <h1 className="font-display text-5xl md:text-6xl tracking-tight mb-5">
            What we&rsquo;ve <span className="italic text-primary">shipped</span>.
          </h1>
          <p className="lead-italic text-foreground/80 max-w-xl">
            Every meaningful change to Myelina Health, features, accuracy improvements, security fixes. In plain English.
          </p>
        </header>

        <ol className="space-y-12 relative">
          {ENTRIES.map((entry) => (
            <li key={entry.date + entry.title} className="border-l-2 border-border/60 pl-6 relative">
              <span
                aria-hidden="true"
                className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full bg-primary"
              />
              <time
                dateTime={entry.date}
                className="block text-[12px] tracking-[0.18em] uppercase text-muted-foreground mb-2"
              >
                {formatDate(entry.date)}
              </time>
              <h2 className="font-display text-2xl md:text-3xl tracking-tight mb-2 text-foreground">
                {entry.title}
              </h2>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {entry.tags.map((t) => (
                  <span
                    key={t}
                    className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] tracking-[0.16em] uppercase font-medium ${TAG_STYLES[t]}`}
                  >
                    {t}
                  </span>
                ))}
              </div>
              <p className="text-[15px] leading-[1.8] text-foreground/85">{entry.body}</p>
            </li>
          ))}
        </ol>

        <p className="mt-20 text-sm text-muted-foreground border-t border-border/40 pt-8">
          Spotted something missing? Email{" "}
          <a className="underline underline-offset-4 hover:text-foreground" href="mailto:hello@myelinahealth.com">
            hello@myelinahealth.com
          </a>{" "}
          and we&rsquo;ll add it.
        </p>
      </main>
      <Footer />
    </div>
  );
};

export default Changelog;
